import { createPortal } from 'react-dom';
import { SmartTooltip } from '../SmartTooltip/SmartTooltip';
import { useGeneratedAxisTooltip } from './hooks/useGeneratedAxisTooltip';
import { toNumberTimestamp } from '../../util.js';
import { convertToZonedTimestamp } from '../../js/helpers/date-helpers/dateHelpers';
import { tooltipEntries } from './js/tooltipEntries';
import type { GeneratedAxisTooltipProps } from './GeneratedAxisTooltip.types';
import './css/generated-axis-chart.css';

export const GeneratedAxisTooltip = ({ tooltip_ref, axes, data, options = {}, line_x, line_y, generated_props }: GeneratedAxisTooltipProps): JSX.Element => {
  const time_zone = generated_props?.time_zone as string | undefined;
  const { pointer, row } = useGeneratedAxisTooltip(tooltip_ref, axes, data, time_zone);
  const horizontal_side = axes.bottom ? 'bottom' : 'top';
  const horizontal = axes[horizontal_side];
  const title_key = horizontal?.value_key;
  const title_value = row && title_key ? row[title_key] : undefined;
  const title_time = horizontal?.scale_type === 'time' ? toNumberTimestamp(title_value) : undefined;
  const axis_title = !row || title_value === undefined || title_value === null ? undefined
    : horizontal?.format?.({ value: title_value as string | number | Date, position: `${horizontal.scale?.(title_value) ?? 0}%` }, 'tooltip', { ...horizontal, time_zone })
      ?? (title_time !== undefined ? convertToZonedTimestamp(title_time, time_zone, 'full') : String(title_value));
  const { title = axis_title, content, children, line_x: option_x, line_y: option_y, snap_to, format, ...settings } = options;
  const show_x = line_x ?? option_x ?? true;
  const show_y = line_y ?? option_y ?? false;
  const body = content !== undefined ? content : children !== undefined ? children : row && (
    <div className='generated-axis-tooltip-entries'>
      {tooltipEntries(row, data ?? [], axes, generated_props).map((entry) => (
        <div key={entry.id} className='generated-axis-tooltip-entry'>
          <span className='generated-axis-tooltip-swatch' style={{ backgroundColor: entry.color }} />
          <span>{entry.name}</span>
          <span className='generated-axis-tooltip-value'>({entry.text}{entry.unit})</span>
        </div>
      ))}
    </div>
  );
  const formatted = row && format ? format(row, 'tooltip', { ...generated_props, axes, data }) : undefined;
  // Only false suppresses formatted output; null/undefined keep the default content.
  const disabled = settings.disabled || formatted === false;
  const vertical = axes.left ?? axes.right;
  const vertical_key = vertical?.value_key;
  const snap_x = row && title_key ? horizontal?.scale?.(row[title_key]) : undefined;
  const snap_y = row && vertical_key ? vertical?.scale?.(row[vertical_key]) : undefined;
  const guide = pointer && snap_to === 'closest' ? {
    x: snap_x !== undefined && Number.isFinite(snap_x) ? Math.max(0, Math.min(100, snap_x)) : pointer.x,
    y: snap_y !== undefined && Number.isFinite(snap_y) ? Math.max(0, Math.min(100, snap_y)) : pointer.y,
  } : pointer;
  const plot = tooltip_ref.current;
  return (
    <>
      {plot && guide && !disabled && (show_x || show_y) && createPortal(
        <div className='generated-axis-crosshairs' aria-hidden='true'>
          {show_x && <div className='generated-axis-crosshair-x' style={{ left: `${guide.x}%` }} />}
          {show_y && <div className='generated-axis-crosshair-y' style={{ top: `${guide.y}%` }} />}
        </div>, plot,
      )}
      <SmartTooltip {...settings} tooltip_ref={tooltip_ref} title={title} content={formatted ?? body} disabled={disabled} />
    </>
  );
};
